import { ReactNode } from 'react';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { Filter, FiltersCollection, isDateRangeFilter } from '@libs/store/filters';
import { FaIcon } from '@atoms/icons';
import { Flex } from '@atoms/layout';
import dayjs from 'dayjs';
import { Box, Fade, Stack } from '@mui/material';
import { ClearAllFiltersButton } from './clearAllButton';
import { FormattedMessage } from 'react-intl';

export type FilterDisplayItem = Filter & { label: ReactNode };
export type FiltersDisplayCollection = Record<string, FilterDisplayItem>;

type ActiveFiltersProps = {
  filters: FiltersDisplayCollection;
  onDelete?: (item: FilterDisplayItem) => void;
};
export function ActiveFilters({ filters, onDelete }: ActiveFiltersProps) {
  const theme = useTheme();

  return (
    <>
      {Object.values(filters).map((f) => (
        <Chip
          variant="outlined"
          key={f.key}
          label={<FilterContent filter={f} />}
          color="primary"
          onDelete={() => onDelete?.(f)}
          deleteIcon={<FaIcon color={theme.palette.primary.main} name="circle-xmark" />}
        />
      ))}
    </>
  );
}

type FilterContentProps = {
  filter: FilterDisplayItem;
};
function FilterContent({ filter }: FilterContentProps) {
  if (isDateRangeFilter(filter)) {
    return (
      <>
        {filter.label}: {dayjs(filter.value.from).format('MMM D, YYYY')} -{' '}
        {dayjs(filter.value.to).format('MMM D, YYYY')}
      </>
    );
  }

  const { label, value, valueView } = filter;
  const view = valueView || value.toString();
  return (
    <Flex gap={0.5} maxWidth={300}>
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      <Typography variant="body2" noWrap title={Array.isArray(view) ? view.join() : view}>
        {view}
      </Typography>
    </Flex>
  );
}

type ActiveFiltersPanelProps = {
  filters: FiltersCollection;
  onDelete?: (item: FilterDisplayItem) => void;
  onClearAll?: () => void;
  localizationSubset?: string;
  ignoreFilters?: string[];
};
export function ActiveFiltersPanel({
  filters,
  onDelete,
  localizationSubset,
  ignoreFilters,
  onClearAll,
}: ActiveFiltersPanelProps) {
  const filtersLocalizeCollection = localizationSubset ? `${localizationSubset}` : 'common.filters';
  const filtersDisplayCollection: FiltersDisplayCollection = Object.keys(filters).reduce<FiltersDisplayCollection>(
    (acc, filterKey) => {
      return (ignoreFilters || []).includes(filterKey)
        ? acc
        : {
            ...acc,
            [filterKey]: {
              ...filters[filterKey],
              label: <FormattedMessage id={`${filtersLocalizeCollection}.${filterKey}.label`} />,
            },
          };
    },
    {},
  );
  const shouldShowClearAllButton = !!Object.keys(filtersDisplayCollection).length;

  if (!shouldShowClearAllButton) {
    return null;
  }

  return (
    <Stack direction={'row'} spacing={2} flexWrap="wrap">
      <ActiveFilters filters={filtersDisplayCollection} onDelete={onDelete} />
      <Box flex={1} />
      <ClearAllFiltersButton localizationSubset={localizationSubset} onClick={onClearAll} />
    </Stack>
  );
}
